import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { useAuth } from './AuthContext';
import config from '../config';
import api from '../utils/api';

// ソケットコンテキストを作成
const SocketContext = createContext();

export function useSocket() {
  return useContext(SocketContext);
}

export function SocketProvider({ children }) {
  const [connected, setConnected] = useState(false);
  const { currentUser, isLoggedIn } = useAuth();
  const [listeners, setListeners] = useState({});
  const [connectionAttempts, setConnectionAttempts] = useState(0);
  const maxReconnectAttempts = 5;
  const [isManuallyConnected, setIsManuallyConnected] = useState(false);
  const [currentRooms, setCurrentRooms] = useState(new Set());
  const lastPollTimestamp = useRef(Date.now());
  const [isMessagePageActive, setIsMessagePageActive] = useState(false);
  const connectionTimeoutRef = useRef(null);
  
  // WebSocket関連の状態
  const [socket, setSocket] = useState(null);
  const [isWebSocketMode, setIsWebSocketMode] = useState(true);
  const [pollingFallback, setPollingFallback] = useState(false);
  const pollingIntervalRef = useRef(null);
  
  // WebSocket接続を開始（無効化 - スマートポーリングを使用）
  const connectWebSocket = () => {
    console.log('🔌 [WEBSOCKET] WebSocket disabled, using smart polling instead');
    
    // WebSocket機能を無効化し、常にポーリングフォールバックを使用
    return false;
  };
  
  // スマートポーリングを開始
  const startPollingFallback = () => {
    console.log('📡 [POLLING] Starting smart polling mode...');
    setIsWebSocketMode(false);
    setPollingFallback(true);
    setConnected(true); // ポーリングでも「接続」状態とする
    setIsManuallyConnected(true);
    
    startPolling();
  };
  
  // メイン接続機能（スマートポーリングのみ使用）
  const connect = () => {
    console.log('🔌 [CONNECTION] Starting smart polling connection...');
    
    if (!isLoggedIn || !currentUser) {
      console.log('❌ [CONNECTION] Cannot connect - not logged in or no user');
      return false;
    }
    
    // WebSocketを無効化し、直接スマートポーリングを開始
    console.log('📡 [CONNECTION] Using smart polling mode');
    startPollingFallback();
    
    return true;
  };

  // 接続を切断
  const disconnectSocket = () => {
    console.log('🔌 [CONNECTION] Disconnecting...');
    setIsManuallyConnected(false);
    setConnected(false);
    setCurrentRooms(new Set());
    setPollingFallback(false);
    
    // WebSocket切断
    if (socket) {
      socket.disconnect();
      setSocket(null);
    }
    
    // ポーリング停止
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  };

  // ポーリングを開始
  const startPolling = () => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }
    
    console.log('📡 [POLLING] Starting polling...');
    
    pollingIntervalRef.current = setInterval(async () => {
      console.log('🔄 [POLLING] Poll cycle check:', {
        isManuallyConnected,
        hasCurrentUser: !!currentUser,
        currentUserId: currentUser?.id,
        isLoggedIn,
        connected,
        pollingFallback
      });
      
      if (!isManuallyConnected || !pollingFallback) {
        console.log('⏸️ [POLLING] Skipping poll - not in polling mode');
        return;
      }
      
      if (!currentUser) {
        console.log('⏸️ [POLLING] Skipping poll - no current user');
        return;
      }
      
      if (!isMessagePageActive) {
        console.log('⏸️ [POLLING] Skipping poll - not on message page');
        return;
      }
      
      console.log('🔄 [POLLING] Executing poll cycle...', {
        currentRooms: Array.from(currentRooms),
        roomCount: currentRooms.size
      });
      
      try {
        await checkForNewMessages();
        await checkForDeletedMessages();
        console.log('✅ [POLLING] Poll cycle completed successfully');
      } catch (error) {
        console.error('❌ [POLLING] Error:', error);
      }
    }, 2000);
    
    console.log('📡 [POLLING] Polling interval started');
  };

  // 新しいメッセージをチェック（ポーリング用）
  const checkForNewMessages = async () => {
    if (currentRooms.size === 0) {
      console.log('⏸️ [POLLING] No rooms to check for new messages');
      return;
    }
    
    try {
      const response = await fetch(`${config.API_BASE_URL}/messages/poll/new-messages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token') || localStorage.getItem('access_token')}`
        },
        body: JSON.stringify({
          rooms: Array.from(currentRooms),
          after: lastPollTimestamp.current
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        
        if (data.messages && data.messages.length > 0) {
          console.log('📨 [POLLING] Found new messages:', data.messages.length);
          
          data.messages.forEach((message) => {
            triggerEvent('new_message', message);
          });
          
          lastPollTimestamp.current = Date.now();
        }
      }
    } catch (error) {
      console.error('❌ [POLLING] Error checking messages:', error);
    }
  };

  // 削除されたメッセージをチェック（ポーリング用）
  const checkForDeletedMessages = async () => {
    if (currentRooms.size === 0) {
      return;
    }
    
    try {
      const response = await fetch(`${config.API_BASE_URL}/messages/poll/deleted-messages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token') || localStorage.getItem('access_token')}`
        },
        body: JSON.stringify({
          rooms: Array.from(currentRooms),
          after: lastPollTimestamp.current
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        
        if (data.deleted_messages && data.deleted_messages.length > 0) {
          console.log('🗑️ [POLLING] Found deleted messages:', data.deleted_messages.length);
          
          data.deleted_messages.forEach((deletedMessage) => {
            triggerEvent('message_deleted', deletedMessage);
          });
        }
      }
    } catch (error) {
      console.error('❌ [POLLING] Error checking deleted messages:', error);
    }
  };

  // イベントリスナーの管理
  const triggerEvent = (event, data) => {
    console.log(`🔔 [EVENT] Triggering event: ${event}`, data);
    if (listeners[event]) {
      listeners[event].forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          console.error(`❌ [EVENT] Error in ${event} listener:`, error);
        }
      });
    }
  };

  // 会話ルームに参加
  const joinConversation = (conversationId) => {
    console.log(`🚪 [ROOM] Joining conversation ${conversationId}`);
    
    if (isWebSocketMode && socket && socket.connected) {
      // WebSocketモード
      socket.emit('join_conversation', { conversation_id: conversationId });
    }
    
    // ルーム管理（ポーリング用）
    setCurrentRooms(prev => new Set([...prev, conversationId]));
    
    console.log(`✅ [ROOM] Joined conversation ${conversationId} (mode: ${isWebSocketMode ? 'WebSocket' : 'Polling'})`);
  };

  // 会話ルームから退出
  const leaveConversation = (conversationId) => {
    console.log(`🚪 [ROOM] Leaving conversation ${conversationId}`);
    
    if (isWebSocketMode && socket && socket.connected) {
      // WebSocketモード
      socket.emit('leave_conversation', { conversation_id: conversationId });
    }
    
    // ルーム管理（ポーリング用）
    setCurrentRooms(prev => {
      const newRooms = new Set(prev);
      newRooms.delete(conversationId);
      return newRooms;
    });
    
    console.log(`✅ [ROOM] Left conversation ${conversationId}`);
  };

  // イベントリスナーの追加
  const on = (event, callback) => {
    console.log(`👂 [LISTENER] Adding listener for event: ${event}`);
    setListeners(prev => ({
      ...prev,
      [event]: [...(prev[event] || []), callback]
    }));
  };

  // イベントリスナーの削除
  const off = (event) => {
    console.log(`👂 [LISTENER] Removing listeners for event: ${event}`);
    setListeners(prev => {
      const newListeners = { ...prev };
      delete newListeners[event];
      return newListeners;
    });
  };

  // イベントの送信
  const emit = (event, data) => {
    console.log(`📤 [EMIT] Emitting event: ${event}`, data);
    
    if (isWebSocketMode && socket && socket.connected) {
      // WebSocketモード
      socket.emit(event, data);
    } else {
      console.log('⚠️ [EMIT] Cannot emit - not in WebSocket mode or not connected');
    }
  };

  // メッセージページの状態制御（デバウンス付き）
  const setMessagePageActive = (isActive) => {
    console.log(`📱 [PAGE] Message page active request: ${isActive}`);
    
    // 既存のタイムアウトをキャンセル
    if (connectionTimeoutRef.current) {
      clearTimeout(connectionTimeoutRef.current);
      connectionTimeoutRef.current = null;
    }
    
    setIsMessagePageActive(isActive);
    
    // デバウンス処理（500ms待機）
    connectionTimeoutRef.current = setTimeout(() => {
      console.log(`📱 [PAGE] Processing debounced request: ${isActive}`);
      
      if (isActive && isLoggedIn && currentUser && !connected) {
        console.log('🔄 [PAGE] Auto-connecting due to message page activation');
        connect();
      } else if (!isActive && connected) {
        console.log('🔄 [PAGE] Disconnecting due to message page deactivation');
        disconnectSocket();
      }
      
      connectionTimeoutRef.current = null;
    }, 500);
  };

  // 自動接続は削除（メッセージページでのみ手動接続）

  // クリーンアップ
  useEffect(() => {
    return () => {
      if (socket) {
        socket.disconnect();
      }
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
      if (connectionTimeoutRef.current) {
        clearTimeout(connectionTimeoutRef.current);
      }
    };
  }, []);

  const value = {
    connected,
    connect,
    disconnectSocket,
    joinConversation,
    leaveConversation,
    on,
    off,
    emit,
    isManuallyConnected,
    isWebSocketMode,
    pollingFallback,
    setMessagePageActive,
    isMessagePageActive
  };

  return (
    <SocketContext.Provider value={value}>
      {children}
    </SocketContext.Provider>
  );
}